<template>
  <div class="certificate-query">
    <div v-if="tabsText.length" class="tabs">
      <span
        v-for="(item, index) in tabsText"
        :key="index"
        :class="{ videoActive: tabsActive === index }"
        @click="tabsActive = index"
      >
        <img :src="tabsActive === index ? item.iconBgActive : item.iconBg" class="tabs-img">
        {{ item.label }}
      </span>
    </div>

    <div class="certificate-list">
      <div v-for="item in listData" :key="item.id" class="certificate-card">
        <div class="certificate-head">
          <div>
            <div class="certificate-name">{{ item.activityName || item.certificateName || "证书" }}</div>
            <div class="certificate-no">证书编号：{{ item.certificateNo || "--" }}</div>
          </div>
          <div class="certificate-badge">{{ item.awardLevelLabel || "证书" }}</div>
        </div>
        <div class="certificate-body">
          <div class="certificate-meta">
            <span>获证人：{{ item.recipientName || "--" }}</span>
            <span>作品：{{ item.workName || "--" }}</span>
          </div>
          <div class="certificate-meta">
            <span>发放时间：{{ formatDate(item.issueTime) }}</span>
            <span>来源：{{ item.sourceTypeLabel || "--" }}</span>
          </div>
        </div>
        <div class="certificate-actions">
          <a-button type="primary" ghost @click="openPreview(item.id)">查看证书</a-button>
        </div>
      </div>

      <div v-if="!listData.length" class="empty-state">暂无证书记录。</div>
    </div>

    <div v-if="total > 0" class="art-pagination">
      <a-pagination
        size="small"
        :total="total"
        :current="params.page"
        :page-size="params.limit"
        @change="onChange"
      />
    </div>

    <a-modal
      title="证书详情"
      :visible="detailVisible"
      :footer="null"
      width="760px"
      @cancel="detailVisible = false"
    >
      <div v-if="detailInfo" class="certificate-preview">
        <div class="preview-inner">
          <div class="preview-header">获奖证书</div>
          <div class="preview-title">{{ detailInfo.activityName || "--" }}</div>
          <div class="preview-subtitle">{{ detailInfo.certificateName || "证书" }}</div>
          <div class="preview-text">
            兹证明
            <strong>{{ detailInfo.recipientName || "--" }}</strong>
            的作品
            <strong>《{{ detailInfo.workName || "--" }}》</strong>
            在本次活动中荣获
            <strong>{{ detailInfo.awardLevelLabel || "--" }}</strong>
            。
          </div>
          <div class="preview-footer">
            <div>证书编号：{{ detailInfo.certificateNo || "--" }}</div>
            <div>发放时间：{{ formatDate(detailInfo.issueTime) }}</div>
          </div>
          <div class="preview-actions">
            <a-button type="primary" :loading="downloadLoading" @click="downloadCurrentPdf">下载 PDF</a-button>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { downloadUserCertificatePdf, userCertificateDetail, userCertificates } from "@/api/UserCenter";

export default {
  name: "CertificateQuery",
  data() {
    return {
      tabsActive: 0,
      tabsText: [
        {
          label: "我的证书",
          iconBg: require("@/assets/img/user/video.png"),
          iconBgActive: require("@/assets/img/user/video-active.png"),
        },
      ],
      listData: [],
      total: 0,
      params: {
        page: 1,
        limit: 8,
      },
      detailVisible: false,
      detailInfo: null,
      downloadLoading: false,
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    fetchList() {
      userCertificates(this.params).then((res) => {
        this.listData = Array.isArray(res.data) ? res.data : [];
        this.total = Number(res.total || 0);
      });
    },
    onChange(page) {
      this.params.page = page;
      this.fetchList();
    },
    openPreview(id) {
      userCertificateDetail(id).then((res) => {
        this.detailInfo = res.data || null;
        this.detailVisible = true;
      });
    },
    downloadCurrentPdf() {
      if (!this.detailInfo || !this.detailInfo.id) {
        return;
      }
      this.downloadLoading = true;
      downloadUserCertificatePdf(this.detailInfo.id)
        .then(async (res) => {
          const blob = new Blob([res.data], {
            type: res.headers["content-type"] || "application/pdf",
          });
          const pdfBlob = await this.normalizePdfBlob(blob);
          const link = document.createElement("a");
          const objectUrl = window.URL.createObjectURL(pdfBlob);
          link.href = objectUrl;
          link.download = this.resolvePdfFileName(res.headers["content-disposition"]);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(objectUrl);
        })
        .catch((err) => {
          this.$message.error((err && (err.msg || err.message)) || "证书下载失败");
        })
        .finally(() => {
          this.downloadLoading = false;
        });
    },
    async normalizePdfBlob(blob) {
      if (!blob) {
        throw new Error("证书下载失败");
      }
      const buffer = await blob.arrayBuffer();
      const header = new Uint8Array(buffer.slice(0, 5));
      const signature = String.fromCharCode(...header);
      if (signature !== "%PDF-") {
        let message = "证书生成失败，请稍后重试";
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          message = parsed.msg || parsed.message || message;
        } catch (e) {
          // ignore json parse failures and keep fallback message
        }
        throw new Error(message);
      }
      return new Blob([buffer], { type: "application/pdf" });
    },
    resolvePdfFileName(contentDisposition) {
      const fallbackBase =
        (this.detailInfo && (this.detailInfo.certificateNo || this.detailInfo.certificateName)) || "证书";
      if (!contentDisposition) {
        return `${fallbackBase}.pdf`;
      }
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1]);
        } catch (error) {
          return utf8Match[1];
        }
      }
      const normalMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      return normalMatch && normalMatch[1] ? normalMatch[1] : `${fallbackBase}.pdf`;
    },
    formatDate(value) {
      return value ? String(value).slice(0, 19).replace("T", " ") : "--";
    },
  },
};
</script>

<style lang="scss">
.certificate-query {
  .tabs {
    height: 47px;
    border-bottom: 1px solid #f2f2f2;
    display: flex;

    span {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      min-width: 88px;
      line-height: 1;
      font-size: 15px;
      color: #7f7f7f;
      padding: 20px 0 13px;
      margin-right: 34px;
      user-select: none;
      cursor: pointer;
      white-space: nowrap;

      .tabs-img {
        flex: 0 0 auto;
        width: 14px;
        height: 14px;
      }
    }

    span.videoActive {
      color: #da514e;
      border-bottom: 2px solid #da514e;
    }
  }

  .certificate-list {
    padding-top: 28px;
  }

  .certificate-card {
    border: 1px solid #f0e2da;
    border-radius: 16px;
    background: linear-gradient(180deg, #fffaf7 0%, #fff 100%);
    padding: 22px 24px;
    margin-bottom: 16px;
  }

  .certificate-head {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  .certificate-name {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: #2f2f2f;
  }

  .certificate-no {
    margin-top: 8px;
    color: #8d7b72;
    font-size: 13px;
  }

  .certificate-badge {
    flex-shrink: 0;
    height: 34px;
    line-height: 34px;
    padding: 0 14px;
    border-radius: 999px;
    background: #f7e1d5;
    color: #b9552f;
    font-weight: 600;
  }

  .certificate-body {
    margin-top: 18px;
  }

  .certificate-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 10px;
    color: #666;
    font-size: 14px;
  }

  .certificate-actions {
    margin-top: 18px;
  }

  .empty-state {
    padding: 40px 0;
    text-align: center;
    color: #999;
  }

  .certificate-preview {
    padding: 12px;
    background: linear-gradient(135deg, #f7e4cf 0%, #fff7ef 100%);
    border-radius: 20px;
  }

  .preview-inner {
    border: 1px solid rgba(191, 118, 66, 0.3);
    border-radius: 16px;
    padding: 42px 36px;
    text-align: center;
    background: rgba(255, 255, 255, 0.72);
  }

  .preview-header {
    font-size: 34px;
    font-weight: 700;
    color: #8c4d28;
    letter-spacing: 6px;
  }

  .preview-title {
    margin-top: 20px;
    font-size: 24px;
    line-height: 36px;
    color: #2f2f2f;
    font-weight: 600;
  }

  .preview-subtitle {
    margin-top: 12px;
    font-size: 18px;
    color: #9b603c;
  }

  .preview-text {
    margin-top: 36px;
    font-size: 18px;
    line-height: 34px;
    color: #4d423b;
  }

  .preview-footer {
    margin-top: 44px;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    color: #7d6a60;
    font-size: 14px;
  }

  .preview-actions {
    margin-top: 18px;
    text-align: center;
  }
}
</style>
